import React from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useWatch } from 'react-hook-form';
import Modal, { ModalSize } from '@ssg/common/Components/Modal';
import Button from '@ssg/common/Components/Button';
import {
	GetDebitorDelayScalepointConnectionsDocument,
	GetDebitorDelayScalepointConnectionsQuery,
	GetDebitorDelayScalepointConnectionsQueryVariables,
	useCreateDebitorDelayScalepointConnectionMutation,
	useGetAdminDamageCausesQuery,
	useGetAllEcbDebitorsQuery,
} from '@ssg/common/GraphQL/indexV2';
import * as yup from 'yup';
import SearchableSelect from '@ssg/common/Components/SearchableSelect';
import { getDebitorName } from './DebitorScalepointSetup';
import { SelectOption } from '@ssg/common/Helpers/Helpers';
import TextButton from '@ssg/common/Components/TextButton';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from 'react-i18next';

export const CreateDebitorDelayScalepointConnectionSchema = yup.object().shape({
	debitorId: yup.string().required(),
});
interface Props {
	close: () => unknown;
}

const DebitorScalepointCreate: React.FC<Props> = ({ close }) => {
	const { t } = useTranslation();
	const { data: allEcbDebitorsData } = useGetAllEcbDebitorsQuery();

	const [searchText, setSearchText] = React.useState<string>('');
	const debitorSelectOptions = React.useMemo(() => {
		return (
			allEcbDebitorsData?.allECBDebitors
				.filter(d => searchText.length === 0 || getDebitorName(d).toLowerCase().includes(searchText.toLowerCase()))
				.map(d => ({
					label: getDebitorName(d),
					value: d.debitorId,
				})) ?? []
		);
	}, [allEcbDebitorsData, searchText]);

	const handleDebitorChange = (value: string): void => {
		if (value === '') return;
		setValue('debitorId', value);
	};

	const { data: allDamageCausesData } = useGetAdminDamageCausesQuery();

	const [selectedDamageCauses, setSelectedDamageCauses] = React.useState<SelectOption[]>([]);
	const [damageCauseSearchText, setDamageCauseSearchText] = React.useState<string>('');
	const searchedDamageCauses = React.useMemo(() => {
		return (
			allDamageCausesData?.damageCausesAdmin.map(dc => ({
				value: dc.id,
				label: `${dc.category.name} - ${dc.name}`,
			})) ?? []
		)
			.filter(dc => damageCauseSearchText.length === 0 || dc.label.toLowerCase().includes(damageCauseSearchText.toLowerCase()))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [allDamageCausesData, damageCauseSearchText]);

	const handleDamageCauseChange = (value: string): void => {
		if (value === '') return;
		setValue('damageCauseIds', [...selectedDamageCauses.map(dc => dc.value), value]);
		setSelectedDamageCauses([...selectedDamageCauses, { value, label: searchedDamageCauses.find(dc => dc.value === value)?.label ?? '' }]);
	};

	const [createDebitorScalepointConnection, { loading }] = useCreateDebitorDelayScalepointConnectionMutation();

	const { handleSubmit, control, setValue } = useForm<{
		debitorId: string;
		damageCauseIds: string[];
	}>({
		resolver: yupResolver(CreateDebitorDelayScalepointConnectionSchema),
		mode: 'all',
		defaultValues: {
			damageCauseIds: [],
		},
	});

	const onSubmit = async ({ debitorId, damageCauseIds }: { debitorId: string; damageCauseIds: string[] }) => {
		await createDebitorScalepointConnection({
			variables: {
				debitorId,
				damageCauseIds,
			},
			update: (cache, { data }): void => {
				if (typeof data === 'undefined' || data === null) {
					return;
				}

				const cachedRequest = cache.readQuery<GetDebitorDelayScalepointConnectionsQuery, GetDebitorDelayScalepointConnectionsQueryVariables>({
					query: GetDebitorDelayScalepointConnectionsDocument,
				});
				if (cachedRequest === null || cachedRequest.debitorDelayScalepointConnections === null) {
					return;
				}

				cache.writeQuery<GetDebitorDelayScalepointConnectionsQuery>({
					query: GetDebitorDelayScalepointConnectionsDocument,
					data: {
						debitorDelayScalepointConnections: [data.createDebitorDelayScalepointConnection, ...cachedRequest.debitorDelayScalepointConnections],
					},
				});
			},
		});
		close();
	};

	const debitorIdWatch = useWatch<string>({
		control,
		name: 'debitorId',
	});

	return (
		<Modal
			size={ModalSize.SMALL}
			title="common.create"
			visible
			close={close}
			body={
				<form onSubmit={handleSubmit(onSubmit)}>
					<div style={{ minHeight: 400 }} className="flex flex-col justify-between">
						<div>
							<SearchableSelect
								required
								control={control}
								name="debitorId"
								title="debitor.overviewTitle"
								options={debitorSelectOptions}
								searchFn={searchText => setSearchText(searchText)}
								onSelect={value => handleDebitorChange(value)}
								onBlur={() => undefined}
								minInputLength={-1}
								isLoading={false}
							/>

							<SearchableSelect
								control={control}
								name="damageCauseIds"
								title="questionnaire.damageCauses"
								options={searchedDamageCauses.filter(u => !selectedDamageCauses.some(s => s.value === u.value))}
								searchFn={searchText => setDamageCauseSearchText(searchText)}
								onSelect={value => handleDamageCauseChange(value)}
								onBlur={() => undefined}
								minInputLength={-1}
								isLoading={false}
								disabled={(debitorIdWatch ?? '').length === 0}
							/>
							{selectedDamageCauses.length === 0 && (debitorIdWatch ?? '').length > 0 && <p className="font-bold ml-1 mt-1">{t('debitor.allSelected')}</p>}

							<ul className="w-full space-y-1 py-1" style={{ maxHeight: 300, overflowY: 'auto' }}>
								{selectedDamageCauses.map(damageCause => (
									<li key={damageCause.value} className="flex items-center">
										<div className="inline-block">
											<TextButton
												icon={faTimes}
												onClick={() => {
													setSelectedDamageCauses(selectedDamageCauses.filter(u => u.value !== damageCause.value));
													setValue(
														'damageCauseIds',
														selectedDamageCauses.filter(u => u.value !== damageCause.value).map(dc => dc.value),
													);
												}}
											/>
										</div>
										<span className="ml-1">{damageCause.label}</span>
									</li>
								))}
							</ul>
						</div>
						<div>
							<Button success submit text="common.create" loading={loading} />
						</div>
					</div>
				</form>
			}
		/>
	);
};
export default DebitorScalepointCreate;

